import { useEffect, useRef, useState } from "react";
import { CaptureEngine } from "./capture-engine.js";
import type {
  BlobLike,
  MediaStreamLike,
  OpenConstraints,
  RecorderLike,
} from "./capture-engine.js";

/**
 * The microphone fork, as this package sees it: attach a read-only consumer to
 * an already-open stream, get 16 kHz mono frames, and get back the function that
 * detaches it (FR-31).
 *
 * The implementation is the host's, and it is the one piece of the capture path
 * that is — an audio worklet in both, loaded from a URL each host's own bundler
 * emits, which is the whole of what stops it being shared code.
 *
 * The desktop ran a `ScriptProcessorNode` on the main thread until one-window
 * ticket 04, and that was a defensible trade rather than an oversight: its
 * microphone lived in the control window and its prompter in another one, so the
 * audio callback had a thread almost to itself, and a worklet costs a second
 * entry point in a three-target Electron build. One window puts them together.
 * On both hosts now, an audio callback on the main thread would compete with
 * laying out and painting a scrolling prompter of one span per word — and a
 * dropped buffer is a dropped word, which is advance latency, the number with
 * the thinnest margin against its bar.
 */
export type AudioTap = (
  stream: MediaStreamLike,
  onFrame: (samples: Float32Array) => void,
) => () => void;

/**
 * What a host can put on screen: the camera held open with no take in progress,
 * or why it could not be (web-ui ticket 04).
 *
 * A pair rather than a bare stream because a preview that cannot open is not an
 * edge case here — it is three of the green room's five states. First visit,
 * blocked, and no hardware are all "there is no stream, and the reason is what
 * the screen is about", and a host given only `null` cannot tell them apart.
 */
export interface CapturePreview {
  /** The live stream, or null when there is none to show. */
  stream: MediaStreamLike | null;
  /** Why there is none, already phrased as the thing to go and do about it —
   * `captureFailureMessage`. Null when nothing has failed. */
  error: string | null;
}

const NO_PREVIEW: CapturePreview = { stream: null, error: null };

/**
 * Bind the {@link CaptureEngine} to the real browser APIs, in the one window
 * that has them (ticket 12).
 *
 * Everything here is adapter: `MediaRecorder`'s event properties become the
 * engine's handler methods, a `MediaStream` becomes the engine's narrow stream
 * port, the host's {@link AudioTap} becomes the microphone fork (ticket 14), and
 * commands and events become contract calls. The engine never sees a browser type,
 * which is what lets it be tested in Node.
 *
 * There is exactly one engine per window and it outlives every render, which is
 * why the tap is read through a ref and the effect has no dependencies. Making
 * the tap a dependency instead would mean a host that passes an inline function —
 * the ordinary thing to write — rebuilding the engine on every render, and a
 * rebuild mid-take drops the recorder and the take with it. The tap is a host
 * constant, so reading the latest one at fork time costs nothing and the hazard
 * stops being something callers have to know about.
 *
 * `mimePreferences` is the container order this host wants, and it is read once
 * for the same reason: it is a constant of the host, not of a render. Omitted, it
 * is the desktop's — see `CaptureEnginePorts.mimePreferences`.
 *
 * `preview` is the opposite kind of argument, and the only one here that is meant
 * to change: it is what the host wants held open with no take in progress, read
 * on every render, so turning the camera off in the green room is a render rather
 * than a call. Omitted — which is what the desktop does — nothing is ever opened
 * outside a take and the returned pair stays empty, so that host is untouched by
 * all of this.
 */
export function useCapture(
  tapAudio: AudioTap,
  mimePreferences?: readonly string[],
  preview?: OpenConstraints | null,
): CapturePreview {
  const tap = useRef(tapAudio);
  tap.current = tapAudio;
  const preferences = useRef(mimePreferences);
  preferences.current = mimePreferences;
  const engine = useRef<CaptureEngine | null>(null);
  const [self, setSelf] = useState<CapturePreview>(NO_PREVIEW);

  useEffect(() => {
    const built = new CaptureEngine({
      ...(preferences.current
        ? { mimePreferences: preferences.current }
        : {}),
      // The one `getUserMedia` call site in the whole app — FR-31's invariant. A
      // take asks for both devices; rehearsal and a camera-off preview ask for
      // one, and an absent key is a device that is not wanted at all.
      getUserMedia: (constraints: OpenConstraints) =>
        navigator.mediaDevices.getUserMedia({
          ...(constraints.video ? { video: constraints.video } : {}),
          ...(constraints.audio ? { audio: constraints.audio } : {}),
        }),
      createRecorder: (stream, mimeType) =>
        adaptRecorder(
          new MediaRecorder(
            stream as MediaStream,
            mimeType ? { mimeType } : undefined,
          ),
        ),
      isTypeSupported: (type) => MediaRecorder.isTypeSupported(type),
      tapAudio: (stream) =>
        tap.current(stream, (samples) =>
          window.cuekards.sendSpeechFrame(samples),
        ),
      send: (event) => window.cuekards.sendCaptureEvent(event),
      // A stream is state the host renders from, so it arrives as state rather
      // than as a return value the effect cannot reach.
      previewChanged: (stream) => setSelf({ stream, error: null }),
      previewFailed: (error) => setSelf({ stream: null, error }),
    });
    engine.current = built;

    const unsubscribe = window.cuekards.onCaptureCommand((command) => {
      void built.handle(command);
    });
    return () => {
      unsubscribe();
      if (engine.current === built) engine.current = null;
      // Give the devices back. Nothing else will: a take is main's and survives a
      // render, but a preview belongs to the surface showing it, and an engine
      // nobody holds any more cannot be asked to close one later. It also makes
      // React's development double-mount safe — the first engine's camera is shut
      // before the second one asks for it, which is FR-31's invariant across a
      // remount rather than only within one.
      void built.preview(null);
    };
  }, []);

  // Deliberately without a dependency array: `preview` is an object a host builds
  // per render out of its device settings, so it is a different object every time
  // and no dependency list could tell an unchanged request from a changed one.
  // The engine compares it structurally and returns immediately when it has not
  // moved, so an unchanged request costs one comparison rather than a queued
  // device open — and there is no second copy of that comparison here to drift
  // from it.
  useEffect(() => {
    void engine.current?.preview(preview ?? null);
  });

  return self;
}

/** `MediaRecorder` behind the engine's port. */
function adaptRecorder(recorder: MediaRecorder): RecorderLike {
  return {
    get mimeType() {
      // What the recorder settled on, which is not always what was asked for —
      // and it is this that the remux is keyed on.
      return recorder.mimeType;
    },
    start: (timesliceMs) => recorder.start(timesliceMs),
    pause: () => recorder.pause(),
    resume: () => recorder.resume(),
    stop: () => recorder.stop(),
    onDataAvailable: (listener: (chunk: BlobLike) => void) => {
      recorder.ondataavailable = (event) => listener(event.data);
    },
    onStop: (listener) => {
      recorder.onstop = () => listener();
    },
    onError: (listener) => {
      recorder.onerror = (event) => {
        // The `DOMException` inside the event is what carries a name worth
        // reporting; the event wrapper stringifies to nothing useful.
        const error = (event as unknown as { error?: unknown }).error;
        listener(error ?? event);
      };
    },
  };
}
